/** @format */

import { Chain, Environment } from '@shared/data-access/interfaces';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import {
  Scene7Dao,
  Scene7MediaSetItemMin,
  Scene7MediaSetResponse,
} from '@shared/media/data-access';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { HttpService } from './http.service';
import { Observable } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class Scene7Service {
  private readonly chain = inject(Chain);
  private readonly environment = inject(Environment);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly scene7Dao = inject(Scene7Dao);
  private readonly httpService = inject(HttpService);

  /**
   * @description
   * On NP0 prod hosts, the GolfGalaxy test CDN must resolve to the real GolfGalaxy CDN.
   */
  private resolveImageCdn(): string | undefined {
    const cdnName = this.environment.thirdParty.scene7?.baseConfig?.cdn;
    const hostName = this.httpService.getHostName();
    const origin = this.httpService.getRequestHeaderValueAndSetStateKey('ORIGIN_HOST');
    const seoIdentifier = this.chain.seoIdentifier;

    return cdnName === 'GolfGalaxyTest/' &&
      (origin?.includes(`prod-${seoIdentifier}`) || hostName.includes(`localhost`))
      ? 'GolfGalaxy/'
      : cdnName;
  }

  /**
   * @description
   * Gets an image set from Scene7 for a given product and supplied arguments
   */
  getProductImageSet$(
    eCode: string,
    swatchColor = '',
    swatchColorPrefix = '',
    mediaSetSuffix = '',
    retries?: number,
  ): Observable<Scene7MediaSetResponse> {
    return this.scene7Dao.getProductImageSet$(
      eCode,
      swatchColor,
      swatchColorPrefix,
      mediaSetSuffix,
      retries,
      this.resolveImageCdn(),
    );
  }

  /**
   * @description
   * Gets image set names from Scene7 for a given product and supplied arguments
   */
  getProductImageSetNames$(
    eCode: string,
    swatchColor = '',
    swatchColorPrefix = '',
    mediaSetSuffix = '',
    retries?: number,
  ): Observable<Scene7MediaSetItemMin[]> {
    return this.scene7Dao.getProductImageSetNames$(
      eCode,
      swatchColor,
      swatchColorPrefix,
      mediaSetSuffix,
      retries,
      this.resolveImageCdn(),
    );
  }

  /**
   * @description
   * Returns the image URL for a Product given certain parameters
   */
  getProductImageUrl(
    eCode: string,
    inCatalogEntryTypeCode = 'productbean',
    inColor: string | null | undefined = '',
    inImagePreset = '',
    inParameters: Record<string, string | number> = {},
    useImageSuffix = true,
  ): string {
    // Set the image suffix
    const imageSuffix =
      typeof inCatalogEntryTypeCode === 'string' &&
      inCatalogEntryTypeCode.toLowerCase() !== 'bundlebean' &&
      useImageSuffix
        ? '_is'
        : '';

    // The Product URL
    let productUrl = '';

    // Query string
    let queryString = '';

    const imageCdn = this.resolveImageCdn();

    // Set the Protocol
    productUrl += this.environment.thirdParty.scene7?.baseConfig?.secureBaseUrl ?? '';

    // Set the Image Directory
    productUrl += this.environment.thirdParty.scene7?.baseConfig?.imageDirectory ?? '';

    // Set the CDN
    productUrl += imageCdn ?? '';

    // Set the eCode
    productUrl += encodeURIComponent(eCode);
    // Set the Color
    const color = inColor ?? '';
    productUrl += color.trim() === '' ? '' : `_${color}`;

    // Set the image suffix
    productUrl += imageSuffix;

    // Check if image preset is provided
    /* istanbul ignore else */
    if (typeof inImagePreset === 'string' && inImagePreset.trim() !== '') {
      // Set the image preset
      queryString += `${inImagePreset}`;
    } else if (inParameters === Object(inParameters)) {
      // Build the query parameters
      queryString += Object.keys(inParameters)
        .map((key) => {
          return `${encodeURIComponent(key)}=${encodeURIComponent(inParameters[key])}`;
        })
        .join('&');
    }

    // Check if query string was built
    if (typeof queryString === 'string' && queryString.trim() !== '') {
      // Set the query string
      productUrl += `?${queryString}`;
    }

    // Return the URL
    return productUrl;
  }

  getColorSwatchImageUrl(ecode: string, color: string, queryParamString: string): string {
    let imagePath = DCSG_CONSTANTS.baseProductImagePath;

    if (
      isPlatformBrowser(this.platformId) &&
      window.location.hostname.includes(`dksxchange`) &&
      !window.location.hostname.includes(`prod-`)
    ) {
      imagePath = `${imagePath}GolfGalaxyTest`;
    } else {
      imagePath = `${imagePath}dkscdn`;
    }

    return `${imagePath}/${ecode}_${color}_swatch/?$DSG_ProductSwatch$${queryParamString}`;
  }

  /**
   * @description
   * Helps create a url string for scene 7 images
   *
   * @param imageName - string of the scene 7 image name with any query strings
   * @param imagePreset - preset to use for the image
   */
  scene7Url(imageName: string, imagePreset = 'HOMRxDEFAULT', isContent = false) {
    const params = this.environment.imagePresets ? `?$${imagePreset}$` : '';
    if (imageName && (imageName.includes('scene7.com') || imageName.includes('wcsstore'))) {
      if (this.environment.thirdParty?.images?.flags.useProxy) {
        imageName = imageName.replace('//dks.scene7.com', '//images.dickssportinggoods.com');
      }
      return imageName;
    } else {
      const url =
        this.environment.thirdParty.images?.scene7proxy[
          isContent ? 'marketingContent' : 'marketing'
        ] ?? '';
      return `${url}${imageName}${params}`;
    }
  }

  setImageWidthAndHeight(imageUrl: string, height: number, width: number) {
    if (!imageUrl.includes('hei') && !imageUrl.includes('wid')) {
      const queryCharacter = imageUrl.includes('?') ? '&' : '?';
      return `${imageUrl}${queryCharacter}wid=${width}&hei=${height}`;
    }
    return imageUrl;
  }
}
